import { Col, Row } from 'antd';
import React, { useEffect, useMemo } from 'react';
import { translate } from '@portal/services/i18n';
import * as MessageService from '@portal/services/message';

import { createOperators } from '@portal/actions/user';
import { getDeviceCount } from '@portal/actions/device';
import { useDispatch } from 'react-redux';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { addMask, maskPhoneInput, unmaskField } from '@portal/services/masks';
import AdvancedSelect from '@portal/components/AdvancedSelect/AdvancedSelect';
import Header from '@portal/components/Header/Header';
import { encrypt } from '@portal/repositories/securitySdkConfig';

import useReduxState from '@portal/hooks/useReduxState';
import { listAllGroups } from '@portal/actions/group';
import { UNSET_CREATE_OPERATOR } from '@portal/actions/actionTypes';
import { getRouteStackPath } from '@portal/config/routes';
import { capitalize } from '@mui/material';
import { Interval } from 'luxon';
import { passwordRules } from '@portal/config/app.json';
import { useNavigation } from '@portal/hooks/useNavigation';

export interface IFormData {
  name: string;
  email: string;
  birthDate: string;
  phone: string;
  document: string;
  group: { value: number; name: string };
  login: string;
  password: string;
}

const isValidDate = (date: string) => {
  if (date) {
    const value = new Date(
      date.replace(/^(\d{1,2}\/)(\d{1,2}\/)(\d{4})$/, '$2$1$3') ?? ''
    );

    return value instanceof Date && !Number.isNaN(new Date(value).getUTCDate());
  }
  return false;
};

const validateYearsOld = (bornAt: string) => {
  const userDate = new Date(
    bornAt.replace(/^(\d{1,2}\/)(\d{1,2}\/)(\d{4})$/, '$2$1$3')
  );
  const currentDate = new Date();
  const diffDates = Interval.fromDateTimes(userDate, currentDate);

  return diffDates.length('years') >= 18;
};

const CreateUserManager = () => {
  const schema = yup
    .object({
      name: yup
        .string()
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        )
        .test(
          'name',
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.SURNAME'
          ),
          (name) => (name && name.split(' ')[1] ? true : false)
        ),

      email: yup
        .string()
        .email(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.INVALID_FORMAT'
          )
        )
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        ),
      birthDate: yup
        .string()
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        )
        .min(
          10,
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.BIRTHDATE_FORMAT'
          )
        )
        .max(
          10,
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.BIRTHDATE_FORMAT'
          )
        )
        .test(
          'birthDate',
          translate('PAGES.USER_MANAGER.CREATE_USER_SCREEN.INVALID_DATE'),
          (birthDate) => isValidDate(birthDate ?? '')
        )
        .test(
          'birthDate',
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.BIRTHDATE_18_YEARS'
          ),
          (birthDate) => {
            if (birthDate && birthDate?.length >= 10) {
              return validateYearsOld(birthDate ?? '');
            } else return false;
          }
        ),
      phone: yup
        .string()
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        )
        .min(
          11,
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.CELLPHONE_FORMAT'
          )
        ),
      document: yup
        .string()
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        )
        .min(
          9,
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.DOCUMENT_FORMAT'
          )
        ),
      group: yup
        .string()
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        ),
      login: yup
        .string()
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        ),
      password: yup
        .string()
        .required(
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.REQUIRED'
          )
        )
        .min(
          passwordRules.minLength,
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.PASSWORD_MIN_LENGTH'
          )?.replace('{{minLength}}', `${passwordRules.minLength}`)
        )
        .max(
          passwordRules.maxLength,
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.PASSWORD_MAX_LENGTH'
          )?.replace('{{maxLength}}', `${passwordRules.maxLength}`)
        )
        .test(
          'password',
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.PASSWORD_LOWERCASE'
          ),
          (pass) => {
            if (!pass) return false;
            return /[a-z]/g.test(pass) || !passwordRules.lowerCase;
          }
        )
        .test(
          'password',
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.PASSWORD_UPPERCASE'
          ),
          (pass) => {
            if (!pass) return false;
            return /[A-Z]/g.test(pass) || !passwordRules.upperCase;
          }
        )
        .test(
          'password',
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.PASSWORD_NUMBER'
          ),
          (pass) => {
            if (!pass) return false;
            return /\d/g.test(pass) || !passwordRules.atLeastOneNumber;
          }
        )
        .test(
          'password',
          translate(
            'PAGES.USER_MANAGER.CREATE_USER_SCREEN.VALIDATE_FORM.PASSWORD_SPECIAL_CHARACTER'
          ),
          (pass) => {
            if (!pass) return false;
            return (
              /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/g.test(pass) ||
              !passwordRules.specialCharacter
            );
          }
        ),
    })
    .required();

  const dispatch = useDispatch();

  const {
    user: { hasCreatedOperator },
    group: { myGroups },
    device: { deviceCount },
  } = useReduxState();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const listGroups = useMemo(() => {
    return myGroups.map((item) => {
      return {
        ...item,
        name: capitalize(item.name),
        value: item.id,
      };
    });
  }, [myGroups]);

  const navigate = useNavigation();

  const onSubmit = async (data: IFormData) => {
    if (deviceCount) {
      const availableSlots = deviceCount.maxDevices - deviceCount.actualDevices;
      if (availableSlots === 0) {
        MessageService.error(
          'PAGES.MY_ACCOUNT.MY_DEVICES.NO_SLOTS_AVAILABLE_WARNING'
        );
        return;
      }
    }

    const encryptedPassword = await encrypt(data.password);
    dispatch(createOperators({ ...data, password: encryptedPassword }));
  };

  useEffect(() => {
    dispatch(listAllGroups(100, 0, ''));
    dispatch(getDeviceCount());
  }, [dispatch]);

  useEffect(() => {
    if (hasCreatedOperator) {
      setTimeout(() => {
        dispatch({
          type: UNSET_CREATE_OPERATOR,
        });
        navigate(getRouteStackPath('USER_MANAGER', 'USER_LIST'));
      }, 1500);
    }
  }, [hasCreatedOperator, dispatch, navigate]);

  return (
    <form
      onSubmit={handleSubmit((data, e) => {
        e?.preventDefault();
        onSubmit(data);
      })}
    >
      <Col className="create-user-manager">
        <Header
          subtitle={translate('PAGES.USER_MANAGER.CREATE_USER_SCREEN.TITLE')}
        />

        <Row>
          <Col className="create-user-manager__padding_col" sm={15}>
            <Controller
              name="name"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedInput
                  value={value}
                  onChange={onChange}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.NAME'
                  )}
                />
              )}
            />
            <p>{errors?.name?.message}</p>
          </Col>
        </Row>

        <Row>
          <Col className="create-user-manager__padding_col" sm={15}>
            <Controller
              name="email"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedInput
                  value={value}
                  onChange={onChange}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.EMAIL'
                  )}
                />
              )}
            />
            <p>{errors?.email?.message}</p>
          </Col>
        </Row>

        <Row>
          <Col className="create-user-manager__padding_col" sm={7}>
            <Controller
              name="birthDate"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedInput
                  maxLength={10}
                  value={addMask(
                    value ?? '',
                    translate('APPLICATION.DEFAULT_DATE_FORMAT.MASK')
                  )}
                  onChange={onChange}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.BIRTHDATE'
                  )}
                />
              )}
            />
            <p>{errors?.birthDate?.message}</p>
          </Col>
          <Col sm={1} />
          <Col className="create-user-manager__padding_col" sm={7}>
            <Controller
              name="phone"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedInput
                  value={maskPhoneInput(value ?? '')}
                  onChange={(value) => {
                    onChange(unmaskField(value ?? ''));
                  }}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.PHONE'
                  )}
                />
              )}
            />
            <p>{errors?.phone?.message}</p>
          </Col>
        </Row>

        <Row>
          <Col className="create-user-manager__padding_col" sm={7}>
            <Controller
              name="document"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedInput
                  value={addMask(
                    value ?? '',
                    translate('APPLICATION.DOCUMENT')
                  )}
                  onChange={onChange}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.DOCUMENT'
                  )}
                />
              )}
            />
            <p>{errors?.document?.message}</p>
          </Col>
          <Col sm={1} />
          <Col className="create-user-manager__padding_col" sm={7}>
            <Controller
              name="group"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedSelect
                  value={value}
                  options={listGroups}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.GROUP'
                  )}
                  onChange={(value) => {
                    onChange(value);
                  }}
                />
              )}
            />

            <p>{errors?.group?.message}</p>
          </Col>
        </Row>

        <div className="create-user-manager__container-left">
          <p className="create-user-manager__container-left__subtitle">
            {translate('PAGES.USER_MANAGER.CREATE_USER_SCREEN.TITLE_SECTION_2')}
          </p>
          <hr />
        </div>

        <Row>
          <Col className="create-user-manager__padding_col" sm={7}>
            <Controller
              name="login"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedInput
                  value={value}
                  onChange={onChange}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.LOGIN'
                  )}
                />
              )}
            />
            <p>{errors?.login?.message}</p>
          </Col>
          <Col sm={1} />
          <Col className="create-user-manager__padding_col" sm={7}>
            <Controller
              name="password"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AdvancedInput
                  securedTextField={true}
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                  }}
                  label={translate(
                    'PAGES.USER_MANAGER.CREATE_USER_SCREEN.INPUTS.PASSWORD'
                  )}
                />
              )}
            />
            <p>{errors?.password?.message}</p>
          </Col>
        </Row>

        <Row>
          <Col sm={12} />
          <Col className="create-user-manager__padding_col" sm={3}>
            <AdvancedButton
              height="3.438rem"
              fullWidth={true}
              type="submit"
              text={translate('PAGES.USER_MANAGER.CREATE_USER_SCREEN.SAVE_BTN')}
            />
          </Col>
        </Row>
      </Col>
    </form>
  );
};

export default CreateUserManager;
